/**
 * Audited reasons, measured exactly as the server measures them.
 *
 * The server refuses a reason whose VISIBLE length is below its verb's
 * minimum (backend/app/core/reasons.py). A dialog that counted
 * `reason.trim().length` disagreed with it both ways: ten zero-width spaces
 * passed the old 10-character dialog (the server refuses them), and a halt
 * dialog asked for 10 characters where the server needs one visible
 * character (architect re-review of 3548e65, M1b [G10]).
 *
 * * {@link REASON_MINIMUMS} is the server's rule, read from
 *   `content/reasonMinimums.json` — a mirror of `REASON_MINIMUMS` that
 *   backend/tests/test_safety_visible_reasons.py holds equal. It is never
 *   restated here.
 * * {@link normaliseReason} / {@link visibleLength} are a line-for-line port
 *   of `normalise_reason` / `visible_length`: drop what never renders
 *   (categories Cc, Cf, Cs, Co, Cn, the non-C default-ignorables and two
 *   blank-rendering symbols), drop combining marks with no base, NFC, collapse
 *   whitespace; then count every character except combining marks (a base and
 *   its marks count once), and whitespace alone counts 0. "Whitespace" is
 *   Python's `str.isspace()` set, not JavaScript's `\s` (which includes
 *   U+FEFF and omits U+001C–U+001F and U+0085). Both sides are checked
 *   against the same vectors (frontend/tests/reason_vectors.json).
 */
import rule from '@/content/reasonMinimums.json';

export type ReasonVerb = keyof typeof rule.minimums;

/** Visible characters each verb's reason needs: 1 reduces risk, 3 adds it. */
export const REASON_MINIMUMS: Readonly<Record<ReasonVerb, number>> = rule.minimums;

export function reasonMinimum(verb: ReasonVerb): number {
  return REASON_MINIMUMS[verb];
}

/** Python's `str.isspace()` code points (Unicode bidi WS/B/S or category Zs). */
const PY_SPACE_CLASS =
  '\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const SPACE = new RegExp(`^[${PY_SPACE_CLASS}]$`, 'u');
const SPACE_RUN = new RegExp(`[${PY_SPACE_CLASS}]+`, 'u');
/** Categories that never render as text. */
const DROPPED = /^[\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}]$/u;
/** Combining marks: non-spacing, spacing and enclosing (Mn, Mc, Me). */
const MARK = /^\p{M}$/u;
/** Default_Ignorable_Code_Point members outside the C categories, plus the
 *  blank-rendering U+2800 and U+1D159 — the server's `_BLANK_RANGES`. */
const BLANK_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x034f, 0x034f],
  [0x115f, 0x1160],
  [0x17b4, 0x17b5],
  [0x180b, 0x180d],
  [0x180f, 0x180f],
  [0x2800, 0x2800],
  [0x3164, 0x3164],
  [0xfe00, 0xfe0f],
  [0xffa0, 0xffa0],
  [0x1d159, 0x1d159],
  [0xe0100, 0xe01ef],
];

function isBlank(char: string): boolean {
  const point = char.codePointAt(0) ?? 0;
  return BLANK_RANGES.some(([low, high]) => low <= point && point <= high);
}

function renders(char: string): boolean {
  if (SPACE.test(char)) return true; // a word separator, collapsed below
  return !DROPPED.test(char) && !isBlank(char);
}

/** Remove combining marks with no base: at the start or after whitespace. */
function dropBaselessMarks(chars: readonly string[]): string {
  const kept: string[] = [];
  let hasBase = false;
  for (const char of chars) {
    if (SPACE.test(char)) {
      hasBase = false;
    } else if (MARK.test(char)) {
      if (!hasBase) continue;
    } else {
      hasBase = true;
    }
    kept.push(char);
  }
  return kept.join('');
}

/** The reason as the server stores it: visible text, NFC, single-spaced. */
export function normaliseReason(text: string): string {
  const kept = dropBaselessMarks(Array.from(text).filter(renders));
  return kept
    .normalize('NFC')
    .split(SPACE_RUN)
    .filter((word) => word.length > 0)
    .join(' ');
}

/** How many characters of a reason a reader sees (the server's measure). */
export function visibleLength(text: string): number {
  const counted = Array.from(normaliseReason(text)).filter((char) => !MARK.test(char));
  if (!counted.some((char) => !SPACE.test(char))) return 0;
  return counted.length;
}

/** Whether a reason meets a minimum, counted as the server counts it. */
export function meetsReasonMinimum(text: string, minimum: number): boolean {
  return visibleLength(text) >= minimum;
}

/** "1 visible character" / "3 visible characters". */
export function visibleCharacters(count: number): string {
  return `${count} visible character${count === 1 ? '' : 's'}`;
}
